// Evidence tab — server-side search/sort/filter + infinite pagination.
//
// The list never holds more than what the user actually scrolled through:
// each "Load more" click fetches the next 100 rows from the server. All
// filtering (q, type, sort) runs in Postgres — never on the loaded
// array — so the controls work correctly even on engagements with
// thousands of evidences.

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import {
  type EvidenceFilters,
  useEngagementEvidence,
} from "@/hooks/useEngagementDetail";
import { friendlyToolName } from "@/lib/toolLabels";
import { useDict, fmt } from "@/i18n/LocaleProvider";

function confidenceColor(pct: number): string {
  if (pct >= 80) return "rgb(var(--color-success))"; // green
  if (pct >= 50) return "rgb(var(--color-warning))"; // amber
  return "rgb(var(--color-muted))";                // muted
}

type SortKey = "newest" | "oldest" | "type" | "title" | "severity";

// Map UX-level sort options to the server's (sort_by, order) pair. "newest"
// uses the endpoint's default (created_at desc) so we send nothing.
const SORT_PARAMS: Record<SortKey, Pick<EvidenceFilters, "sort_by" | "order">> = {
  newest:   {},
  oldest:   { sort_by: "created_at", order: "asc" },
  type:     { sort_by: "evidence_type", order: "asc" },
  title:    { sort_by: "title", order: "asc" },
  severity: { sort_by: "severity_suggested", order: "desc" },
};

export function EvidenceTab({ engagementId }: { engagementId: string }) {
  const dd = useDict();
  const tr = dd.app.evidence;
  const eng = dd.app.engagements;
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const filters: EvidenceFilters = {
    q: q.trim() || undefined,
    evidence_type: typeFilter === "all" ? undefined : typeFilter,
    ...SORT_PARAMS[sort],
  };

  const {
    data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useEngagementEvidence(engagementId, filters);

  if (isLoading) return <Loading text={tr.loading} />;

  const evidence = data?.pages.flatMap((p) => p.evidences) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  // Types seen in the loaded pages — used for the chip strip. With
  // server-side filtering this is just a discoverability shortcut; the
  // user can also still type/sort to find anything.
  const types = Array.from(new Set(evidence.map((e) => e.evidence_type)));

  return (
    <div className="flex flex-col gap-4">
      {/* Search + sort row. Search hits Postgres ILIKE on title+description. */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <Input
          value={q}
          onChange={setQ}
          placeholder={tr.searchPlaceholder}
          className="flex-1"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="font-mono text-[11px] bg-bg2 border border-stroke rounded px-2 py-2 text-content"
        >
          <option value="newest">{tr.sortNewest}</option>
          <option value="oldest">{tr.sortOldest}</option>
          <option value="type">{tr.sortType}</option>
          <option value="title">{tr.sortTitle}</option>
          <option value="severity">{tr.sortSeverity}</option>
        </select>
      </div>

      {/* Type filter chips. Server-side — clicking re-fetches page 1. */}
      <div className="flex gap-2 flex-wrap">
        <Button
          small
          variant={typeFilter === "all" ? "primary" : "default"}
          onClick={() => setTypeFilter("all")}
        >
          {fmt(tr.all, { n: total })}
        </Button>
        {types.map((t) => (
          <Button
            key={t}
            small
            variant={typeFilter === t ? "primary" : "default"}
            onClick={() => setTypeFilter(t)}
          >
            {t}
          </Button>
        ))}
      </div>

      {evidence.length === 0 ? (
        <Empty text={tr.noMatch} />
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {evidence.map((e) => {
              const pct = Math.round(e.confidence_score * 100);
              const cColor = confidenceColor(pct);
              return (
                <Card key={e.id} className="!p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-sans text-[14px] text-content font-semibold leading-[1.4] break-words">
                        {e.title}
                      </div>
                      <div className="font-mono text-[10px] text-muted uppercase tracking-[0.05em] mt-2">
                        <span className="text-accent">{friendlyToolName(e.source_tool)}</span>
                        <span className="mx-2 text-dim">·</span>
                        <span>{e.evidence_type}</span>
                      </div>
                    </div>
                    <div
                      className="font-mono text-[11px] font-semibold px-2 py-1 rounded shrink-0"
                      style={{
                        color: cColor,
                        background: `${cColor}11`,
                        border: `1px solid ${cColor}55`,
                      }}
                    >
                      {pct}%
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="font-mono text-[10px] text-muted">
              {fmt(eng.showing, { n: evidence.length, total })}
            </div>
            {hasNextPage && (
              <Button
                small
                variant="default"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                {isFetchingNextPage ? eng.loadingMore : eng.loadMore}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
